import React, {useEffect} from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useAuth } from "../../context/auth";
import {useHistory} from "react-router-dom";
import {
    ACL_ENABLE_ENDPOINTS,
    DQ_PRO_PLAN_KEY,
    LOCAL_STORAGE_KEYS,
    TOAST_TYPES,
    URL_PERMS_MAP,
    USER_ACCESS_LEVEL,
    PRODUCT_KEY,
    FEATURE_KEY
} from "../../utils/constants";
import {toast} from "../../utils/utils";
import { isProductEnabled, isFeatureEnabled, canViewProduct, canCreateProduct } from "../../utils/productAccess";
import { isLcTeacher, isClientWorkspace, canAuthorLearningProgram } from "../../utils/capabilities";


const PrivateRouteWithLayout = props => {
    const { layout: Layout, component: Component, ...rest } = props;
    const { authToken, authUser } = useAuth();
    const history = useHistory();

    useEffect(()=> {
        if (!authUser) {
            toast(TOAST_TYPES.DANGER, "Forbidden", "you are not authorized to access this page, \nPlease login using valid credentials!")
        }

        if (authUser && props.location.pathname !== '/auth/confirm/email') {
            if (!authUser.is_email_verified) {
                toast(TOAST_TYPES.DANGER, "Email Not Verified", "you are not a legitimate user in our system, please verify your email address first")
            }
        }

    }, [authUser]);

    useEffect( ()=> {
        const lcStorageUser = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_user));
        if (lcStorageUser && lcStorageUser.is_email_verified && !['/password/change', '/logout'].includes(props.location.pathname)) {
            if (lcStorageUser.required_password_reset && authUser.required_password_reset) {
                toast(TOAST_TYPES.INFO, "Next Step", "Please update your temporary password.")
                history.push('/password/change');
            }
        }
    }, [props.location.pathname] )

    const isPermissible = (pathname) => {
        if (authUser) {
            /*
             * Every rule below that was written for a school (level 22) applies
             * unchanged to a school network, whose contact sits at 21 — see
             * isClientWorkspace. The one genuine difference, the My Schools list,
             * is handled separately by the is_school_network line in
             * restrictedPaths further down.
             */

            // Level 25: Individual learner — personal AI-Digital Safety dashboard
            // only. They share the main account's insider profile (and its plan),
            // so without this allowlist the plan-based menus/pages of the MAIN
            // account (Billing, Peers, Clients…) would be reachable.
            if (authUser.access_level === USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL) {
                // Educator workspace is not for individual learners — send them to
                // their personal dashboard even when login or a stale bookmark
                // lands on /my_workspace or /dq_pro/home.
                if (pathname === '/my_workspace' || pathname.startsWith('/dq_pro/home')) {
                    return '/dq_pro/individual-dashboard/score-overview';
                }
                const allowedPrefixes = [
                    '/dq_pro/individual-dashboard',
                    '/account/settings',
                    '/password/change',      // forced temp-password reset flow
                    // No '/support' — Support is Partner Workspace (access level
                    // 20) only, per Ian Ryu's design-review note on PW-ALL-Support.
                    '/logout',
                ];
                if (!allowedPrefixes.some((p) => pathname.startsWith(p))) {
                    return '/dq_pro/individual-dashboard/score-overview';
                }
                return true;
            }

            // Level 23: LC Teacher — created by a school, scoped to the classes
            // bound on their membership. An ALLOWLIST, deliberately placed second
            // for the same reason level 25 is first: a teacher shares the school's
            // insider profile and plan, so anything not listed here would be
            // reachable through the level-22 rules further down. Fail-closed.
            //
            // Authoring and the Program Impact Dashboard gate on the CAPABILITY,
            // not on this level — a teacher without `create_learning_program` is
            // still a teacher, just one who cannot build or distribute.
            if (authUser.access_level === USER_ACCESS_LEVEL.TEACHER_ACCESS_DQ_INSIDER_PORTAL) {
                const canAuthor = isLcTeacher(authUser);

                // Authoring + distribution + programme impact: LC Teachers only.
                const lcOnlyPrefixes = [
                    '/dq_pro/program/creation',
                    '/dq_pro/program/distribute',
                    '/dq_pro/program-impact-dashboard',
                    '/dq_pro/lc-program-impact-dashboard',
                    // The now-unused /dq_pro/learncreator placeholder route — kept
                    // gated defensively in case a stale bookmark reaches it.
                    '/dq_pro/learncreator',
                    // The "My Programs > +Create" wizard entry (DQProCreate.page.jsx) —
                    // a different authoring surface from the sidebar's own
                    // LearnCreator item, which is covered by the
                    // '/dq_pro/program/creation' prefix above (it points at
                    // /dq_pro/program/creation/preview, the real in-app builder).
                    '/dq_pro/create',
                ];
                if (lcOnlyPrefixes.some((p) => pathname.startsWith(p))) {
                    return canAuthor ? true : 'my_workspace';
                }

                // Solutions > COS Children Assessment > Preview hands off to this
                // real, read-only question-by-question preview screen (see
                // SolutionChildrenAssessmentPreview.page.jsx). Scoped to the exact
                // .../preview suffix, not the bare /dq_pro/product/view/:code base
                // route (DQProAssessmentDetailsPage) — that one is a management
                // page and stays blocked for a teacher.
                if (/^\/dq_pro\/product\/view\/[^/]+\/preview$/.test(pathname)) {
                    return true;
                }

                const teacherPrefixes = [
                    '/my_workspace',
                    '/dq_pro/solutions',               // read-only Solutions section (Common Section)
                    '/dq_pro/classes',                 // their own classes — filtered in-page
                    '/dq_pro/learning/program',        // their own programs — filtered server-side
                    '/dq_pro/products/program',        // "View Program" from that list — was missing, silently bounced to my_workspace
                    '/dq_pro/class-dashboard',
                    '/account/settings',
                    '/password/change',                // forced temp-password reset flow
                    // No '/support' — Support is Partner Workspace (access level
                    // 20) only, per Ian Ryu's design-review note on PW-ALL-Support.
                    '/logout',
                ];
                if (!teacherPrefixes.some((p) => pathname.startsWith(p))) {
                    return 'my_workspace';
                }
                return true;
            }

            // P1 Workspace — the school→partner learning program request loop
            // (owner's request list + the school's request form). This flow ships
            // with the enterprise_p1 plan ONLY, so no enterprise_p0 user reaches
            // it, by menu or by direct URL. Checked before every other rule so it
            // holds for owners and schools alike.
            const p1RequestPaths = ['/dq_pro/program/requests', '/dq_pro/program/request'];
            if (p1RequestPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
                return authUser.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P1
                    ? true
                    : 'my_workspace';
            }

            /*
             * Growth Opportunities > Product Adoption > My Program Adoption.
             *
             * NOT GATED, deliberately. The sheet tags this row P1B, LC — narrower
             * than its ALL-tagged sibling Solution Adoption — and it used to bounce
             * anyone else back to Home. But every page under Growth Opportunities is
             * still a WorkspacePlaceholder, which says "Coming soon" and shows no
             * data, so there is nothing there to protect and a redirect only leaves
             * the reader wondering what they did wrong. The sidebar row is
             * marked not-selectable for the same reason.
             *
             * Put the entitlement check back the day the real page lands.
             */

            // Growth Opportunities > Portfolio Snapshot > Attention Needed — the
            // sheet tags this row P0, P1 (no LC, unlike its Executive Overview
            // sibling). "P1" here means the whole P1 family (base plan plus both
            // product-access variants), not split by P1A/P1B.
            if (pathname.startsWith('/dq_pro/growth-opportunities/portfolio-snapshot/attention-needed')) {
                return [
                    DQ_PRO_PLAN_KEY.ENTERPRISE_P0,
                    DQ_PRO_PLAN_KEY.ENTERPRISE_P1,
                    DQ_PRO_PLAN_KEY.ENTERPRISE_P1A,
                    DQ_PRO_PLAN_KEY.ENTERPRISE_P1B,
                ].includes(authUser.dq_pro_plan?.plan_key)
                    ? true
                    : 'my_workspace';
            }

            // Program creation preview — accessible to anyone with learning
            // program (view) permission, regardless of package/plan and
            // ahead of the stricter "create" gate the rest of the creation
            // flow requires below.
            if (pathname.startsWith('/dq_pro/program/creation/preview')) {
                // A P1 school may view its partner's programs without the view
                // flag (same rule as programViewPaths below). Now that opening a
                // program skips the dashboard, the preview is where it lands.
                const isP1School = isClientWorkspace(authUser)
                    && authUser.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P1;
                return (isP1School || canViewProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM)) ? true : 'my_workspace';
            }

            // Level 22: Partial Access Single Institute
            if (isClientWorkspace(authUser)) {
                // Learning program — gate by granular permissions:
                //   create -> program creation flow (incl. the creation preview/setup step)
                //   view   -> program list + existing-program view/preview/dashboard pages
                //   Both halves of the server's rule, not just the product one:
                //   productGuard(learning_program, create) AND
                //   capabilityGuard(create_learning_program). A school lead who
                //   passes only the first was let in here and then met a 403
                //   CAPABILITY_REQUIRED on submit. A network passes the second
                //   implicitly as a workspace owner.
                if (pathname.startsWith('/dq_pro/program/creation')) {
                    return canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM)
                        && canAuthorLearningProgram(authUser)
                        ? true : 'my_workspace';
                }
                const programViewPaths = [
                    '/dq_pro/learning/program',
                    '/dq_pro/products/program/dashboard',
                    '/dq_pro/products/program/view',
                    '/dq_pro/products/program/',
                ];
                if (programViewPaths.some(p => pathname.startsWith(p))) {
                    // P1 Workspace flow: a school views the learning programs its DQ
                    // Partner delivered to it — a core capability of that plan — so a
                    // P1 school is allowed here even without the explicit product-view
                    // flag. Every other level-22 user still needs the flag.
                    const isP1School = authUser.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P1;
                    return (isP1School || canViewProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM)) ? true : 'my_workspace';
                }
                /**
                 * /account/settings is NO LONGER restricted here.
                 *
                 * It was, because that page is where the workspace owner's
                 * subscription, payment method, credits and invoices live — and a
                 * level-22 school inherits the owner's dq_pro_plan, so it would
                 * have been reading the partner's commercial data.
                 *
                 * The sheet gives School its own Settings (SW_PI_ST, rows
                 * 144-146) with three tabs: Account, Security, Advanced Settings.
                 * No Billing and Payments. Blocking the whole route also denied a
                 * school its own name and password, which the sheet grants.
                 *
                 * The billing tabs are now withheld at the page instead — see
                 * `isSchool` in AccountSettings.page.jsx. Withhold the data, not
                 * the page. If that tab filter is ever removed, this path has to
                 * come back into the list.
                 */
                const restrictedPaths = [
                    '/dq_pro/products',
                    '/dq_pro/program/creation',
                    '/dq_pro/assessment/creation',
                    '/dq_pro/peers/list',
                    // A SCHOOL has no client list. A SCHOOL NETWORK does — it is
                    // the "My Schools" row of the SNW sidebar (Navigation Bar
                    // sheet row 66), and the list is scoped server-side to the
                    // schools inside that network. Both are level 22, so the
                    // is_school_network flag on the session is what separates them.
                    ...(authUser.is_school_network ? [] : ['/dq_pro/clients/list']),
                ];
                // Educator onboarding Step 1 lands on the CPCI suite preview page.
                const isCpciSuitePath = pathname.startsWith('/dq_pro/products/cpci/suite');
                if (!isCpciSuitePath && restrictedPaths.some(p => pathname.startsWith(p))) return false;
            }

            // Free users (no active paid plan): show 404 for Peers and Classes routes
            const isPaidUser = authUser.dq_pro_plan?.status === 'active' && authUser.dq_pro_plan?.plan_key !== DQ_PRO_PLAN_KEY.DEFAULT;
            if (!isPaidUser && pathname.startsWith('/dq_pro/peers/list')) return 'not-found';
            if (!isPaidUser && pathname.startsWith('/dq_pro/classes')) return 'not-found';

            // Product Access Control Enforcements
            const requiresLearning = [
                '/dq_pro/learning/program',
                '/dq_pro/products/program',
                '/dq_pro/program/creation'
            ];
            const requiresAssessment = [
                '/dq_pro/assessment',
                '/dq_pro/products/assessments',
                '/dq_pro/products/cpci',
            ];

            // P1 Workspace flow: a school (level-22 under a P1 owner) must be able to
            // open the learning programs its DQ Partner delivered. Product access is
            // resolved per-user, and the school carries no learning_program flag of its
            // own — the owner holds it — so without this it would be redirected away
            // from programs its own plan is built around. Read-only paths only:
            // /program/creation stays gated by the create checks above.
            const isP1SchoolViewer =
                isClientWorkspace(authUser)
                && authUser.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P1
                && !pathname.startsWith('/dq_pro/program/creation');

            if (requiresLearning.some(p => pathname.startsWith(p))
                && !isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM)
                && !isP1SchoolViewer) {
                return 'my_workspace';
            }
            // Step 1 of the P1 school's workspace is "Preview Assessments", which
            // points at the COS suite. Product access is per-user and the school
            // carries no assessment flag of its own (the owner holds it), so this
            // gate bounced it straight back to my_workspace — the card looked dead
            // and, because Step 1 never completed, Step 2 stayed locked forever.
            // Preview only: the suite page is read-only, and assessment CREATION
            // is still blocked by the level-22 restrictedPaths check above.
            const isP1SchoolAssessmentViewer =
                isClientWorkspace(authUser)
                && authUser.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P1
                && pathname.startsWith('/dq_pro/products/cpci');

            if (requiresAssessment.some(p => pathname.startsWith(p)) && !(
                isProductEnabled(authUser, PRODUCT_KEY.ASSESSMENT)
                || isProductEnabled(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT)
            ) && !isP1SchoolAssessmentViewer) {
                return 'my_workspace';
            }
            if (pathname.startsWith('/dq_pro/create')) {
                const hasAnyProgramAccess = isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM)
                    || isProductEnabled(authUser, PRODUCT_KEY.ASSESSMENT)
                    || isProductEnabled(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT);
                if (!hasAnyProgramAccess) return 'my_workspace';
            }

            // Board dashboards (PAC features). Server scopes owner → partner_dashboard,
            // client → school_dashboard. Gate direct URL access.
            if (pathname.startsWith('/dq_pro/national-dashboard')
                && !isFeatureEnabled(authUser, FEATURE_KEY.NATIONAL_DASHBOARD)) {
                return 'my_workspace';
            }
            if (pathname.startsWith('/dq_pro/partner-dashboard')
                && authUser.access_level !== USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL
                && !isFeatureEnabled(authUser, FEATURE_KEY.PARTNER_DASHBOARD)) {
                return 'my_workspace';
            }
            if (pathname.startsWith('/dq_pro/school-dashboard')
                && !isFeatureEnabled(authUser, FEATURE_KEY.SCHOOL_DASHBOARD)) {
                return 'my_workspace';
            }
            if (pathname.startsWith('/dq_pro/class-dashboard')
                && !isFeatureEnabled(authUser, FEATURE_KEY.CLASS_DASHBOARD)) {
                return 'my_workspace';
            }
            if (pathname.startsWith('/dq_pro/individual-dashboard')
                && authUser.access_level !== USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL
                && !isFeatureEnabled(authUser, FEATURE_KEY.INDIVIDUAL_DASHBOARD)) {
                return 'my_workspace';
            }
            if (pathname.startsWith('/dq_pro/insights')
                && isClientWorkspace(authUser)
                && !isFeatureEnabled(authUser, FEATURE_KEY.SCHOOL_DASHBOARD)
                && !isProductEnabled(authUser, PRODUCT_KEY.IMPACT_DASHBOARD)) {
                return 'my_workspace';
            }

            // Support is the Partner Workspace (access level 20) and — since the
            // SNW, SW Parental Consent Flow (5 Oct 2026) — the School and School
            // Network workspaces, whose Support page carries the Parental Consent
            // card. Teacher and individual workspaces have none (their
            // allowlists above already leave it out). This closes direct-URL
            // access for roles whose sidebar never shows the link.
            if (pathname.startsWith('/support')
                && authUser.access_level !== USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL
                && !isClientWorkspace(authUser)) {
                return 'my_workspace';
            }

            // Parental Consent set-up is a school's or network's own — a partner
            // has no learners of its own to set it up for. The API refuses a
            // caller without an organisation of its own, too.
            if (pathname.startsWith('/dq_pro/parental-consent') && !isClientWorkspace(authUser)) {
                return 'my_workspace';
            }

            // Existing logic
            if (authUser.permissions && authUser.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS) {
                const perms = authUser.permissions;
                const path_parts = pathname.split('/');
                if (path_parts.length > 2) {
                    const effective_path = [path_parts[1], path_parts[2]].join('/');
                    if (ACL_ENABLE_ENDPOINTS.includes(effective_path)) {
                        const url_perm_key = URL_PERMS_MAP[effective_path];
                        return !!perms[url_perm_key];
                    }
                }


            }
        }
        return true
    }

    return (
        <Route
            {...rest}
            render={matchProps => (
                authToken && authUser ? (
                    authUser.is_email_verified || matchProps.location.pathname === '/auth/confirm/email' ? (
                            (() => {
                                const perm = isPermissible(matchProps.location.pathname);
                                if (perm === true) return <Layout><Component {...matchProps} /></Layout>;
                                if (perm === 'not-found') return <Redirect to={{ pathname: '/not-found', referer: matchProps.location }} />;
                                // Absolute-path results redirect to that exact page
                                // (e.g. individual learners → their dashboard).
                                if (typeof perm === 'string' && perm.startsWith('/')) {
                                    return <Redirect to={{ pathname: perm, referer: matchProps.location }} />;
                                }
                                return <Redirect to={{ pathname: '/my_workspace', referer: matchProps.location }} />;
                            })()
                        )
                     : <Redirect
                            to={{ pathname: "/auth/confirm/email", referer: matchProps.location}}
                        />
                ) : (
                    <Redirect
                        to={{ pathname: "/login", referer: matchProps.location}}
                    />
                )
            )}
        />
    );

};

export default PrivateRouteWithLayout;
